import { Box, Card, CardContent, Container, Divider, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { EmptyContent, Markdown, Spinner, useUserDetailsStore, uuidv4 } from '@zeta-core';
import { CloseIcon } from '@zeta-core/assets';
import { TranscriptAPIs } from '@zeta-services';
import {
  IClientCardItem,
  IFactFindEntity,
  IViewByIDTranscriptResponse,
  IViewTranscriptConversation,
  IViewTranscriptRequest,
  IViewTranscriptResponse,
  IViewTranscriptTopics,
  TopicType,
  TopicTypeLables,
} from '@zeta-types';

const Transcript = ({
  section,
  additionalSection,
  isTranscript,
}: {
  section: TopicType;
  additionalSection?: TopicType;
  isTranscript: {
    onTrue: () => void;
    onFalse: () => void;
  };
}) => {
  const location = useLocation();
  const state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  const theme = useTheme();
  const { firstName, lastName } = useUserDetailsStore((state) => state.user);

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<IViewTranscriptTopics | null>(null);
  const [additionalTranscript, setAdditionalTranscript] = useState<IViewTranscriptTopics | null>(null);
  const [isAudio, setIsAudio] = useState(false);
  const [startTime, setStartTime] = useState('00:00:00');

  useLayoutEffect(() => {
    (document.body.style as any)['overflow-x'] = 'hidden';
    return () => {
      (document.body.style as any)['overflow-x'] = 'auto';
    };
  }, []);

  const viewTranscriptByID = useMutation({
    mutationFn: (payload: IViewTranscriptRequest) => TranscriptAPIs().viewTranscriptByID(payload),

    onSuccess: (response: AxiosResponse<IViewByIDTranscriptResponse>) => {
      const { data: responseData } = response;
      if (responseData?.audioFile) {
        const formData = new FormData();
        if (transcript?.transcriptionID !== undefined) {
          formData.append('transcriptID', transcript?.transcriptionID);
        }
        streamTranscript.mutateAsync(formData);
        setIsAudio(true);
        setStartTime(transcript?.topicStartTime || '00:00:00');
      }
    },
  });

  const streamTranscript = useMutation({
    mutationFn: async (payload: FormData) => {
      const resp = await TranscriptAPIs().streamTranscript(payload);
      const url = URL.createObjectURL(resp?.data);
      setAudioUrl(url);
    },
  });

  // Cleanup the object URL when component unmounts or audioUrl changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
    };
  }, [audioUrl]);

  const viewTranscript = useMutation({
    mutationFn: (payload: IViewTranscriptRequest) => TranscriptAPIs().viewTranscript(payload),

    onSuccess: (response: AxiosResponse<IViewTranscriptResponse>) => {
      const { data: responseData } = response;
      const id = responseData?.resp?.[0]?.transcriptionID;
      if (responseData?.resp?.[0]) {
        setTranscript(responseData?.resp?.[0]);
      }
      if (id) {
        viewTranscriptByID.mutateAsync({ transcriptId: id });
      }
    },
  });

  const viewAdditionalTranscript = useMutation({
    mutationFn: (payload: IViewTranscriptRequest) => TranscriptAPIs().viewTranscript(payload),

    onSuccess: (response: AxiosResponse<IViewTranscriptResponse>) => {
      const { data: responseData } = response;
      if (responseData?.resp?.[0]) {
        setAdditionalTranscript(responseData?.resp?.[0]);
      }
    },
  });

  useEffect(() => {
    viewTranscript.mutateAsync({ caseID: state.clientData.caseID, topicType: section });
    if (additionalSection) {
      viewAdditionalTranscript.mutateAsync({ caseID: state.clientData.caseID, topicType: additionalSection });
    }
  }, []);

  const combinedTranscript: IViewTranscriptConversation[] = [...(transcript?.conversations || []), ...(additionalTranscript?.conversations || [])].filter(
    (item) => item
  );

  return (
    <Stack sx={{ zIndex: 3 }} gap={2}>
      <Stack flexDirection="row" justifyContent="space-between">
        <Stack>
          <Typography variant="h6">Transcript</Typography>
          <Typography variant="caption" color="text.secondary">
            This transcript provides a detailed record of the fact find meeting between {firstName} {lastName} and {state?.clientData?.retailClientName},
            covering key aspects like income, expenditures, and assets and more...
          </Typography>
        </Stack>
        <IconButton onClick={isTranscript.onFalse}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Card sx={{ border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1.5 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Card sx={{ background: theme.palette.background.neutral, border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 1.5 }}>
            <CardContent>
              <Stack gap={2.5}>
                <Typography variant="body1">Summary of {TopicTypeLables[section]}</Typography>
                {transcript?.topicSummary || additionalTranscript?.topicSummary ? (
                  <>
                    {transcript?.topicSummary && <Markdown children={transcript?.topicSummary ?? ''} />}
                    {additionalTranscript?.topicSummary && <Markdown children={additionalTranscript?.topicSummary ?? ''} />}
                  </>
                ) : (
                  <Typography variant="body2">No Information</Typography>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Stack gap={1} mt={1}>
            {Array.from(combinedTranscript || []).length > 0 ? (
              combinedTranscript.map((item) => {
                return (
                  <Stack key={item?.conversationID}>
                    {item?.conversationText?.map((text) => (
                      <Typography key={uuidv4()} variant="subtitle2" mb={1}>
                        {text?.textSpeaker}: {text?.text}
                      </Typography>
                    ))}
                  </Stack>
                );
              })
            ) : (
              <EmptyContent title={`No Conversation on ${TopicTypeLables[section]}`} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {isAudio && (
        <Box sx={{ position: 'absolute', bottom: 5, width: '80%', overflowX: 'hidden' }}>
          <Container sx={{ width: 'fill-available', ml: 0, px: '0 !important' }} maxWidth="xl">
            {audioUrl ? (
              <audio controls style={{ width: '100%' }}>
                <source src={audioUrl + `#t=${startTime}`} type="audio/mp4" />
                Your browser does not support the audio element.
              </audio>
            ) : (
              <Card variant="outlined">
                <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                  <Box p={1.5}>
                    <Spinner />
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                    <Typography variant="body2" ml={1.5} sx={{ flex: 1 }}>
                      The audio is loading up! Take a moment to review the summary while you wait.{' '}
                    </Typography>
                    <Typography variant="subtitle2" pr={1.5}>
                      0:00:00
                    </Typography>
                  </Stack>
                </Stack>
              </Card>
            )}
          </Container>
        </Box>
      )}
    </Stack>
  );
};

export default Transcript;
